// Event report packet, front section: a one-page executive summary, then the list of everyone whose report is in the
// packet (with the page it starts on, once the PDF worker has laid the packet out). Same letterhead and page frame as
// the individual report; the worker renders this page (?packet=<id>) and puts the reports and documents after it.
// OrgPacketSummary (2026-10-22): the same front pages for one organization's visits on a date or over a date range.
import type { ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { useData } from '../lib/useData.ts';
import { CLINIC, DEV_REPORT, Letterhead, PHONE, us } from './Report.tsx';

export const ROWS_PER_PAGE = 30;   // must match apps/api/src/lib/eventPackets.ts
const CLASSES = ['Excellent', 'Good', 'Average', 'Fair', 'Poor'] as const;

interface Row {
  assessmentId: string; lastName: string; firstName: string; employeeId: string | null; assessedOn: string; status: string;
  respiratorQualified: boolean | null; referred: boolean | null; signedBy: string | null;
  followUp?: { on: string; wasRespiratorQualified: boolean | null; wasReferred: boolean | null } | null;   // decided again on follow-up
  aerobicClass: string | null; riskClass: string | null; fatPct: number | null; vo2: number | null;
}
interface Summary {
  event: { id: string; departmentName: string; startsOn: string; endsOn: string | null; location: string | null;
    hostName?: string; oneOrganization?: boolean; otherOrganizations?: string[];
    range?: boolean; events?: number };   // range: an organization packet (startsOn .. endsOn = the dates chosen)
  included: Row[]; pending: Row[]; notTested: number | null;
  contents: { summaryPages: number; totalPages: number; people: { assessmentId: string; page: number; documents: number }[];
    eventDocuments: { page: number; count: number } | null } | null;
}

const dates = (s: Summary) => (s.event.endsOn && s.event.endsOn !== s.event.startsOn ? `${us(s.event.startsOn)} – ${us(s.event.endsOn)}` : us(s.event.startsOn));
const yn = (v: boolean | null) => (v === null ? '—' : v ? 'Yes' : 'No');
const avg = (xs: (number | null)[]) => { const v = xs.filter((x): x is number => x !== null && Number.isFinite(x)); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };

function Sheet({ n, total, title, s, children }: { n: number; total: number; title: string; s: Summary; children: ReactNode }) {
  return (
    <section className="rpt-page" data-page={`summary-${n}`}>
      {DEV_REPORT && <div className="rpt-dev">DEV · TEST DATA · NOT A MEDICAL RECORD</div>}
      <Letterhead reportDate={us(new Date().toISOString().slice(0, 10))} />
      <h1 className="rpt-title">{title}</h1>
      <div className="rpt-body">{children}</div>
      <footer className="rpt-foot"><span>{CLINIC} · {PHONE}</span><span>{s.event.departmentName} · {dates(s)} · Page {n} of {total}</span></footer>
    </section>
  );
}

function Tally({ label, rows, field: k }: { label: string; rows: Row[]; field: 'aerobicClass' | 'riskClass' }) {
  const with_ = rows.filter((r) => r[k]);
  return (
    <table className="rpt-table es-tally">
      <thead><tr><th>{label}</th>{CLASSES.map((c) => <th key={c}>{c}</th>)}</tr></thead>
      <tbody><tr><td>People ({with_.length})</td>{CLASSES.map((c) => <td key={c}>{with_.filter((r) => r[k] === c).length}</td>)}</tr></tbody>
    </table>
  );
}

export function EventSummary() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const packet = sp.get('packet');
  const org = sp.get('org');   // one of the event's organizations (2026-10-20)
  const { data: s, error } = useData<Summary>(`/api/events/${id}/summary${packet ? `?packet=${packet}` : org ? `?departmentId=${org}` : ''}`);
  if (error) return <div data-report-error={error instanceof Error ? error.message : String(error)}><ErrorBox error={error} /></div>;
  if (!s) return <p className="muted">Loading…</p>;
  return <SummaryView s={s} back={<Link to={`/events/${id}`}>← Back to event</Link>} />;
}

/** One organization's visits from one date to another (?from=&to=), or the packet being built (?packet=). */
export function OrgPacketSummary() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const packet = sp.get('packet');
  const q = packet ? `packet=${packet}` : `from=${sp.get('from') ?? ''}&to=${sp.get('to') ?? ''}`;
  const { data: s, error } = useData<Summary>(`/api/departments/${id}/packet-summary?${q}`);
  if (error) return <div data-report-error={error instanceof Error ? error.message : String(error)}><ErrorBox error={error} /></div>;
  if (!s) return <p className="muted">Loading…</p>;
  return <SummaryView s={s} back={<Link to={`/organizations/${id}`}>← Back to organization</Link>} />;
}

function SummaryView({ s, back }: { s: Summary; back: ReactNode }) {
  const range = !!s.event.range;
  const inc = s.included;
  const listPages = Math.ceil(inc.length / ROWS_PER_PAGE);
  const total = s.contents?.totalPages ?? 1 + listPages;
  const pageOf = new Map(s.contents?.people.map((p) => [p.assessmentId, p]) ?? []);
  const referred = inc.filter((r) => r.referred);
  const notCleared = inc.filter((r) => r.respiratorQualified === false);
  const followedUp = inc.filter((r) => r.followUp);
  const signers = [...new Set(inc.map((r) => r.signedBy).filter(Boolean))];
  const kpi = (n: number | string, label: string, tone = '') => <div className={`es-kpi ${tone}`}><b>{n}</b><span>{label}</span></div>;
  const vo2 = avg(inc.map((r) => r.vo2)), fat = avg(inc.map((r) => r.fatPct));
  // Marked reviewed = waiting for the physician; drafts (and iPad captures not yet reviewed) are not finished yet.
  const awaiting = s.pending.filter((r) => r.status === 'reviewed');
  const unfinished = s.pending.filter((r) => r.status !== 'reviewed');
  const names = (list: Row[]) => list.slice(0, 15).map((r) => `${r.firstName} ${r.lastName}`).join(', ') + (list.length > 15 ? `, and ${list.length - 15} more` : '');
  return (
    <div data-report-ready="" className="rpt">
      <div className="no-print spread" style={{ marginBottom: '1rem' }}>
        {back}
        <span className="muted small">Preview of the packet's front pages. The full packet (with every report and document) is made on the {range ? 'organization' : 'event'} page.</span>
      </div>
      <Sheet n={1} total={total} title={range ? 'Organization Report · Executive Summary' : 'Event Report · Executive Summary'} s={s}>
        <div className="rpt-person">
          <div><div className="rpt-pname">{s.event.departmentName}</div>
            {s.event.oneOrganization && s.event.hostName && s.event.hostName !== s.event.departmentName && <div>At the {s.event.hostName} testing event</div>}
            {!s.event.oneOrganization && !!s.event.otherOrganizations?.length && <div>With {s.event.otherOrganizations.join(', ')}</div>}
            {range
              ? <div>Visits {s.event.endsOn && s.event.endsOn !== s.event.startsOn ? `from ${us(s.event.startsOn)} to ${us(s.event.endsOn)}` : `on ${us(s.event.startsOn)}`}{s.event.events ? ` · ${s.event.events} testing event${s.event.events === 1 ? '' : 's'}` : ''}</div>
              : <div>Testing {dates(s)}{s.event.location ? ` · ${s.event.location}` : ''}</div>}</div>
          <div className="rpt-right">{signers.length > 0 && <>Reviewed and signed by<br /><b>{signers.join(', ')}</b></>}</div>
        </div>
        <div className="es-kpis">
          {kpi(inc.length + s.pending.length, 'Tested')}
          {kpi(inc.length, 'Reports in this packet')}
          {kpi(inc.filter((r) => r.respiratorQualified).length, 'Cleared for respirator use', 'ok')}
          {kpi(notCleared.length, 'Not cleared', notCleared.length ? 'bad' : '')}
          {kpi(referred.length, 'Referred for follow-up', referred.length ? 'warn' : '')}
          {kpi(awaiting.length, 'Awaiting physician review')}
          {kpi(unfinished.length, 'Not finished (draft)')}
          {s.notTested !== null && kpi(s.notTested, 'On the roster, not tested')}
        </div>
        <h2 className="rpt-h">Fitness and risk across the group</h2>
        <Tally label="Aerobic fitness" rows={inc} field="aerobicClass" />
        <Tally label="Coronary risk" rows={inc} field="riskClass" />
        <p className="rpt-intro">Average aerobic capacity {vo2 === null ? '—' : `${vo2.toFixed(1)} ml/kg/min`} · average body fat {fat === null ? '—' : `${fat.toFixed(1)} %`}
          {' '}(of those with the test recorded). Classes run from Excellent to Poor; for coronary risk, higher classes mean lower risk.</p>
        <h2 className="rpt-h">Follow-up</h2>
        {!referred.length && !notCleared.length ? <p className="rpt-intro">No one was referred for follow-up and everyone reviewed was cleared.</p> : (
          <table className="rpt-table es-list">
            <thead><tr><th>Name</th><th>ID #</th><th>Respirator cleared</th><th>Referred</th><th>Page</th></tr></thead>
            <tbody>{[...new Set([...notCleared, ...referred])].slice(0, 8).map((r) => (
              <tr key={r.assessmentId}><td>{r.lastName}, {r.firstName}</td><td>{r.employeeId ?? '—'}</td><td>{yn(r.respiratorQualified)}</td><td>{yn(r.referred)}</td>
                <td>{pageOf.get(r.assessmentId)?.page ?? '—'}</td></tr>))}</tbody>
          </table>)}
        {new Set([...notCleared, ...referred]).size > 8 && <p className="rpt-intro">… and more; see the participant list.</p>}
        {followedUp.length > 0 && <p className="rpt-intro">Updated after follow-up: {followedUp.map((r) => `${r.firstName} ${r.lastName} (${us(r.followUp!.on)})`).join(', ')}. The figures above show the current decisions.</p>}
        {awaiting.length > 0 && <p className="rpt-intro"><b>Awaiting physician review (not in this packet):</b> {names(awaiting)}.</p>}
        {unfinished.length > 0 && <p className="rpt-intro"><b>Not finished (draft, not in this packet):</b> {names(unfinished)}.</p>}
        {s.contents?.eventDocuments && <p className="rpt-intro">Event documents ({s.contents.eventDocuments.count}) begin on page {s.contents.eventDocuments.page}.</p>}
        <p className="rpt-intro es-note">Individual reports follow the participant list, in alphabetical order, each followed by that person's supporting documents.
          Confidential medical information: share only with those authorized to receive it.</p>
      </Sheet>
      {Array.from({ length: listPages }, (_, i) => (
        <Sheet key={i} n={i + 2} total={total} title={`Participants in this packet${listPages > 1 ? ` (${i + 1} of ${listPages})` : ''}`} s={s}>
          <table className="rpt-table es-list">
            <thead><tr><th>#</th><th>Name</th><th>ID #</th><th>Assessed</th><th>Respirator cleared</th><th>Referred</th><th>Aerobic</th><th>Coronary risk</th><th>Docs</th><th>Page</th></tr></thead>
            <tbody>{inc.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE).map((r, j) => {
              const p = pageOf.get(r.assessmentId);
              return (
                <tr key={r.assessmentId}><td>{i * ROWS_PER_PAGE + j + 1}</td><td className="es-name">{r.lastName}, {r.firstName}</td><td>{r.employeeId ?? '—'}</td>
                  <td>{us(r.assessedOn)}</td><td>{yn(r.respiratorQualified)}</td><td>{yn(r.referred)}</td><td>{r.aerobicClass ?? '—'}</td><td>{r.riskClass ?? '—'}</td>
                  <td>{p ? p.documents : '—'}</td><td>{p?.page ?? '—'}</td></tr>);
            })}</tbody>
          </table>
        </Sheet>
      ))}
    </div>
  );
}
